import React, { useCallback, useState } from 'react'
import useSWR from 'swr'
import { fetcher } from 'api/swr'
import Loader from '@/components/ui/Loader'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

type FakeUser = {
  id: string
  name?: string
  photoUrl?: string
  app?: string
  contentLang?: number
  providerId?: string
}

type FakeUsersList = {
  total: number
  items: FakeUser[]
}

const PAGE_SIZE = 20

const FakeUsers: React.FC = () => {
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])

  const goAfter = useCallback((nextAfter: string | null) => {
    if (nextAfter === null) {
      setAfter(null)
      return
    }
    setAfters((prev) => {
      const index = prev.findIndex((item) => item === nextAfter)
      if (index === -1) {
        return [...prev, nextAfter]
      }
      return prev
    })
    setAfter(nextAfter)
  }, [])

  const params = {
    limit: PAGE_SIZE,
    ...(after ? { after } : {}),
  }

  const { data, isLoading, isValidating, mutate } = useSWR<FakeUsersList>(
    ['/fake-users', params],
    fetcher,
  )

  const nextAfter =
    data?.items?.length === PAGE_SIZE
      ? data.items[data.items.length - 1]?.id
      : undefined

  if (isLoading && !data) {
    return <Loader />
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
          />
          <span className="text-sm text-muted-foreground">
            共 {data?.total ?? 0} 个
          </span>
          <MyRefetchButton onClick={() => mutate()} />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        {isValidating ? (
          <Loader />
        ) : !data?.items?.length ? (
          <p className="text-sm text-muted-foreground">暂无 fake user 数据</p>
        ) : (
          <Card className="flex h-full flex-col">
            <CardContent className="flex-1 overflow-hidden pb-0 pl-4 pr-0">
              <div className="h-full overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[72px]">头像</TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>名称</TableHead>
                      <TableHead>App</TableHead>
                      <TableHead>contentLang</TableHead>
                      <TableHead>providerId</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          {user.photoUrl ? (
                            <img
                              src={user.photoUrl}
                              alt={user.name ?? user.id}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {user.id}
                        </TableCell>
                        <TableCell>{user.name ?? '—'}</TableCell>
                        <TableCell>{user.app ?? '—'}</TableCell>
                        <TableCell>{user.contentLang ?? '—'}</TableCell>
                        <TableCell>{user.providerId ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

export default FakeUsers
